'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { OverlayBusyProvider, useOverlayBusyState } from './overlay-busy';
import { OverlayFootContext } from './overlay-foot';
import { attachSwipeToDismiss } from './swipe-to-dismiss';
import styles from './sheet.module.css';

/**
 * Sheet - the mobile menu/dialog substrate (docs/MOBILE.md §4): a native
 * `<dialog>` opened with `showModal()` (real backdrop, inert background,
 * Esc-to-close), with a grabber pill and swipe-to-dismiss. Document scroll
 * is locked while open.
 *
 * Geometry (the drawer-clipped-by-toolbar bug, 2026-07-11): the dialog is a
 * full-viewport flex FRAME sized `100dvh` - the dynamic-viewport unit is the
 * platform's own "track the collapsing browser toolbar" mechanism - and the
 * visible PANEL is a child seated at its end. A top-layer dialog anchored
 * `bottom: 0` can end up against the LAYOUT viewport, behind the toolbar
 * (it did, on iOS); a panel at the end of a dvh frame sits at the VISIBLE
 * bottom by construction, no measurement JS.
 *
 * This is where transient dialogs, menus and confirmations go on a phone
 * (the item actions menu, the create-task form); item detail is NOT a sheet -
 * it is the full-page push. Callers decide when to use it (typically
 * `compact` from `useViewport`); the component itself works at any width.
 *
 * `busy` (ruled 2026-10-18), as on `Dialog`: while a submit taken in the sheet
 * is in flight, Esc, the backdrop tap and the swipe close nothing; the busy
 * control that took the act shows the wait. The caller passes `busy`, or a
 * form inside reports its own flag with `useOverlayBusy` (overlay-busy.ts).
 *
 * `full` (2026-10-21, docs/MOBILE.md "THE SHEET RULES": a sheet may take the
 * full screen): the panel grows to the frame's full height, below the safe
 * area, instead of sizing to its content up to 85%. The caller sets it while
 * the sheet shows a list it is searching (the item's Views picker while its
 * field has focus), so the list has the room the on-screen keyboard leaves.
 */
export function Sheet({
  open,
  onClose,
  label,
  busy: busyProp = false,
  full = false,
  children,
}: {
  open: boolean;
  /** Called for every close path: Esc (cancel), backdrop tap, swipe-down. */
  onClose: () => void;
  label: string;
  /** A submit taken in the sheet is in flight: no close path closes it. Pass
   *  the flag the submit's `pending` reads, or report it from inside with
   *  `useOverlayBusy`. */
  busy?: boolean;
  /** Grow the panel to the full height of the screen (see above). */
  full?: boolean;
  children: ReactNode;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const grabberRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  // The pinned foot below the body, published to the content so an act row
  // nested in it can seat at the visible bottom (`OverlayFoot`).
  const [foot, setFoot] = useState<HTMLElement | null>(null);
  const { busy, report } = useOverlayBusyState(open, busyProp);

  // Drive the native dialog from the `open` prop.
  useEffect(() => {
    const dlg = dialogRef.current;
    if (!dlg) return;
    if (open && !dlg.open) {
      dlg.showModal();
      // showModal's focus delegation lands on the first focusable descendant.
      // A sheet opens to be READ, so initial focus stays on the sheet itself
      // (tabIndex -1 below) for EVERY delegate: a text field would open the
      // on-screen keyboard over the sheet (the "drawer is typing" bug), and
      // a button wears the platform's focus ring on a screen nobody tabbed
      // on (the blue-border report - both 2026-07-11). Tab still walks into
      // the rows for hardware-keyboard users, and a descendant carrying the
      // native `autofocus` attribute still wins - the platform's opt-in.
      const active = document.activeElement;
      if (
        active instanceof HTMLElement &&
        active !== dlg &&
        dlg.contains(active) &&
        !active.hasAttribute('autofocus')
      ) {
        dlg.focus();
      }
    } else if (!open && dlg.open) {
      dlg.close();
    }
  }, [open]);

  // Lock the document scroll while open (showModal inerts but does not lock).
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // While busy the swipe is not attached, so a pull does not move the panel.
  useEffect(() => {
    const panel = panelRef.current;
    if (!open || busy || !panel) return;
    return attachSwipeToDismiss({
      panel,
      grabber: grabberRef.current,
      scroller: bodyRef.current,
      onDismiss: onClose,
    });
  }, [open, busy, onClose]);

  // Esc closes THIS sheet only. A native bubble-phase listener on the dialog,
  // not React's onKeyDown: the App Router mounts React at `document`, so a
  // synthetic handler is delegated to the same node as a Modal peek's own
  // document-level key listener (modal.tsx) and stopPropagation there is too
  // late - one keypress would close both layers.
  useEffect(() => {
    const dlg = dialogRef.current;
    if (!open || !dlg) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') e.stopPropagation();
    };
    dlg.addEventListener('keydown', onKey);
    return () => dlg.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <dialog
      ref={dialogRef}
      className={styles.sheet}
      aria-label={label}
      data-testid="sheet"
      tabIndex={-1}
      onCancel={(e) => {
        // React synthesizes bubbling for `cancel` through the REACT tree, so a
        // NESTED sheet's Esc would fire this handler too and close both layers
        // (found with the access sheet's dials, 2026-07-11) - act only on our
        // own dialog's cancel.
        if (e.target !== dialogRef.current) return;
        // Esc: keep the parent's `open` state the source of truth. While
        // busy it closes nothing.
        e.preventDefault();
        if (!busy) onClose();
      }}
      onClick={(e) => {
        // The dialog is the full-viewport frame (sheet.module.css): a tap
        // anywhere outside the panel targets the dialog itself - dismiss.
        if (e.target === dialogRef.current && !busy) onClose();
      }}
    >
      <OverlayBusyProvider value={report}>
        <div className={styles.panel} ref={panelRef} data-full={full || undefined}>
          <div className={styles.grabber} ref={grabberRef} aria-hidden>
            <span className={styles.pill} />
          </div>
          <div className={styles.body} ref={bodyRef}>
            <OverlayFootContext.Provider value={foot}>{children}</OverlayFootContext.Provider>
          </div>
          <div className={styles.foot} ref={setFoot} data-overlay-foot />
        </div>
      </OverlayBusyProvider>
    </dialog>
  );
}
